import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Settings as SettingsIcon,
  Shield, ShieldCheck, History, KeyRound, Users, ShieldPlus,
  Palette, Globe, Clock, Hash, CheckCircle2, ArrowRight, Sun, Moon, Monitor,
} from "lucide-react";
import { useLocalization } from "../contexts/LocalizationContext";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";

/**
 * Settings — Overview page.
 *
 * Replaces the previous mock UI that had many fake toggles (SMS notifs, session
 * timeout, maintenance mode…). This page now surfaces two clean sections:
 *
 *   1. Access & Security — direct links to the real self-service pages
 *      (Two-factor auth, Audit log, Custom roles, IP whitelist, Team members).
 *
 *   2. Preferences — the display prefs that actually work today (theme, language,
 *      country, timezone, date & number format).
 *
 * Everything else that used to be a fake toggle was removed to avoid misleading
 * the merchant into thinking security is configured when it wasn't.
 */

interface AccessCardProps {
  title: string;
  description: string;
  path: string;
  Icon: React.ElementType;
  accent: "emerald" | "blue" | "indigo" | "amber" | "rose";
  status?: string;
  statusKind?: "ok" | "warn" | "info";
  adminOnly?: boolean;
}

const ACCENTS = {
  emerald: {
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 55%, #047857 100%)",
    glow: "shadow-emerald-500/40 hover:shadow-emerald-500/60",
    blob: "bg-emerald-300/30",
  },
  blue: {
    gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 55%, #1d4ed8 100%)",
    glow: "shadow-blue-500/40 hover:shadow-blue-500/60",
    blob: "bg-sky-300/30",
  },
  indigo: {
    gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 55%, #4338ca 100%)",
    glow: "shadow-indigo-500/40 hover:shadow-indigo-500/60",
    blob: "bg-violet-300/30",
  },
  amber: {
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 55%, #b45309 100%)",
    glow: "shadow-amber-500/40 hover:shadow-amber-500/60",
    blob: "bg-orange-300/30",
  },
  rose: {
    gradient: "linear-gradient(135deg, #f43f5e 0%, #e11d48 55%, #be123c 100%)",
    glow: "shadow-rose-500/40 hover:shadow-rose-500/60",
    blob: "bg-pink-300/30",
  },
};

const AccessCard: React.FC<AccessCardProps> = ({ title, description, path, Icon, accent, status, statusKind, adminOnly }) => {
  const s = ACCENTS[accent];
  const statusStyles = {
    ok:   "bg-white/25 text-white",
    warn: "bg-white/25 text-white",
    info: "bg-white/25 text-white",
  } as const;
  return (
    <Link
      to={path}
      className={`group relative block rounded-2xl p-6 overflow-hidden text-white shadow-xl ${s.glow} hover:-translate-y-1 hover:shadow-2xl transition-all duration-300`}
      style={{ background: s.gradient }}
    >
      {/* Decorative blob */}
      <div className={`pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full blur-2xl ${s.blob}`} />
      {/* Subtle grid overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
        backgroundSize: "22px 22px",
      }} />
      {/* Shimmer sweep on hover */}
      <div className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <div className="relative flex items-start justify-between gap-3">
        <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 shadow-lg shadow-black/10">
          <Icon className="w-6 h-6 text-white" strokeWidth={2.2} />
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm ${statusStyles[statusKind || "info"]}`}>
              {status}
            </span>
          )}
          {adminOnly && (
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/70 bg-white/10 rounded px-1.5 py-0.5">
              Admin
            </span>
          )}
        </div>
      </div>

      <h3 className="relative mt-5 text-base font-bold tracking-tight text-white">{title}</h3>
      <p className="relative mt-1.5 text-[12.5px] leading-relaxed text-white/85 line-clamp-3">{description}</p>

      <div className="relative mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 group-hover:bg-white/25 group-hover:gap-2.5 transition-all">
        Open <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
};

// ---------------------------------------------------------------------------

interface PrefCardProps {
  Icon: React.ElementType;
  accent: "emerald" | "blue" | "indigo" | "amber" | "rose";
  title: string;
  hint: string;
  children: React.ReactNode;
}

const PREF_ACCENTS = {
  emerald: { bar: "bg-gradient-to-r from-emerald-500 to-teal-500", bubble: "bg-emerald-50 text-emerald-600 ring-emerald-100" },
  blue:    { bar: "bg-gradient-to-r from-blue-500 to-sky-500",       bubble: "bg-blue-50 text-blue-600 ring-blue-100" },
  indigo:  { bar: "bg-gradient-to-r from-indigo-500 to-violet-500",   bubble: "bg-indigo-50 text-indigo-600 ring-indigo-100" },
  amber:   { bar: "bg-gradient-to-r from-amber-500 to-orange-500",    bubble: "bg-amber-50 text-amber-600 ring-amber-100" },
  rose:    { bar: "bg-gradient-to-r from-rose-500 to-pink-500",       bubble: "bg-rose-50 text-rose-600 ring-rose-100" },
};

const PrefCard: React.FC<PrefCardProps> = ({ Icon, accent, title, hint, children }) => {
  const s = PREF_ACCENTS[accent];
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
      <div className={`h-1 w-full ${s.bar}`} />
      <div className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${s.bubble} ring-1 flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-5 h-5" strokeWidth={2.1} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 leading-tight">{title}</p>
            <p className="text-[11.5px] text-slate-500 mt-1 leading-snug">{hint}</p>
          </div>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const Settings: React.FC = () => {
  const { language, country, countryConfig, setLanguage, setCountry, t, getAvailableCountries } = useLocalization();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();

  const isAdmin = user?.role === "merchant_admin" || user?.niveau === 0;

  // Preferences that actually take effect (theme, language, country) live in
  // their own contexts and update immediately — no separate save button needed.
  const [dateFormat, setDateFormat] = useState<string>(() =>
    localStorage.getItem("moko_date_format") || "DD/MM/YYYY");
  const [numberFormat, setNumberFormat] = useState<string>(() =>
    localStorage.getItem("moko_number_format") || "european");
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  const savePreference = (key: string, value: string, label: string) => {
    localStorage.setItem(key, value);
    setSavedNotice(label);
    setTimeout(() => setSavedNotice(null), 2000);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <SettingsIcon className="w-6 h-6 text-slate-700" />
          {t("settings.title") || "Settings"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage your account security, review audit trails and adjust your display preferences.
        </p>
      </div>

      {/* ── Access & Security ── */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-slate-500" />
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t("sidebar.section.access") || "Access & Security"}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <AccessCard
            title={t("nav.twofa") || "Two-factor authentication"}
            description="Protect your account with a 6-digit code from an authenticator app (Google Authenticator, 1Password, Authy)."
            path="/settings/2fa"
            Icon={ShieldCheck}
            accent="emerald"
          />
          <AccessCard
            title={t("nav.auditLog") || "Audit log"}
            description="Every security-sensitive action on your account, filterable and exportable to CSV."
            path="/settings/audit-log"
            Icon={History}
            accent="blue"
          />
          <AccessCard
            title={t("nav.ipWhitelist") || "IP whitelist"}
            description="Restrict API calls to a list of trusted IPs. Requests submitted here are validated by Moko admins."
            path="/developers/ip-whitelist"
            Icon={KeyRound}
            accent="indigo"
          />
          {isAdmin && (
            <>
              <AccessCard
                title={t("nav.team") || "Team members"}
                description="Invite team members, revoke access, and assign built-in or custom roles."
                path="/team"
                Icon={Users}
                accent="rose"
                adminOnly
              />
              <AccessCard
                title={t("nav.customRoles") || "Custom roles"}
                description="Define your own role catalog with a precise permission grid; assign to any team member."
                path="/settings/roles"
                Icon={ShieldPlus}
                accent="amber"
                adminOnly
              />
            </>
          )}
        </div>
      </section>

      {/* ── Preferences ── */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Palette className="w-4 h-4 text-slate-500" />
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Preferences</h2>
        </div>

        {/* Theme — featured card (full width) */}
        <div className="relative overflow-hidden rounded-2xl p-6 text-white shadow-xl shadow-slate-900/30 mb-4"
             style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)" }}>
          <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full blur-3xl bg-indigo-500/25" />
          <div className="pointer-events-none absolute -left-10 bottom-0 w-48 h-48 rounded-full blur-3xl bg-emerald-500/20" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }} />
          <div className="relative flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center shadow-lg shadow-black/20">
                <Palette className="w-6 h-6 text-white" strokeWidth={2.2} />
              </div>
              <div>
                <p className="text-lg font-bold tracking-tight">Theme</p>
                <p className="text-xs text-white/70 mt-0.5">Choose how the portal looks — light, dark or follow your system.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl p-1.5">
              {(["light", "dark", "auto"] as const).map((mode) => {
                const Icon = mode === "light" ? Sun : mode === "dark" ? Moon : Monitor;
                const active = theme === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => setTheme(mode)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      active
                        ? "bg-white text-slate-900 shadow-lg shadow-black/30 scale-105"
                        : "text-white/80 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="capitalize">{mode}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Other preferences — colorful grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <PrefCard
            Icon={Globe}
            accent="blue"
            title="Language"
            hint="Interface language."
          >
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as "fr" | "en")}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:ring-4 focus:ring-blue-500/15 focus:border-blue-500 transition-all"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </PrefCard>

          <PrefCard
            Icon={Globe}
            accent="emerald"
            title="Country"
            hint="Sets default currency, timezone and formats."
          >
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value as any)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:ring-4 focus:ring-emerald-500/15 focus:border-emerald-500 transition-all"
            >
              {getAvailableCountries().map((c: any) => (
                <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
              ))}
            </select>
          </PrefCard>

          <PrefCard
            Icon={Clock}
            accent="indigo"
            title="Timezone"
            hint="Derived from your country selection."
          >
            <div className="w-full px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-sm font-mono text-slate-700 flex items-center justify-between">
              <span>{countryConfig.timezone}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Auto</span>
            </div>
          </PrefCard>

          <PrefCard
            Icon={Clock}
            accent="rose"
            title="Date format"
            hint="How dates are displayed across the portal."
          >
            <select
              value={dateFormat}
              onChange={(e) => {
                setDateFormat(e.target.value);
                savePreference("moko_date_format", e.target.value, "Date format saved");
              }}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:ring-4 focus:ring-rose-500/15 focus:border-rose-500 transition-all"
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </PrefCard>

          <PrefCard
            Icon={Hash}
            accent="amber"
            title="Number format"
            hint="Thousand and decimal separators."
          >
            <select
              value={numberFormat}
              onChange={(e) => {
                setNumberFormat(e.target.value);
                savePreference("moko_number_format", e.target.value, "Number format saved");
              }}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:ring-4 focus:ring-amber-500/15 focus:border-amber-500 transition-all"
            >
              <option value="european">European (1.234,56)</option>
              <option value="us">US (1,234.56)</option>
              <option value="space">Space (1 234,56)</option>
            </select>
          </PrefCard>
        </div>
      </section>

      {/* Info footer */}
      <div className="text-[11px] text-slate-400 flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" />
        Preferences apply immediately across your session.
      </div>

      {/* Save notice */}
      {savedNotice && (
        <div className="fixed bottom-6 right-6 px-4 py-3 rounded-lg bg-emerald-600 text-white text-sm shadow-lg z-50">
          {savedNotice}
        </div>
      )}
    </div>
  );
};

export default Settings;
